/**
 * Dropdown Menu
 * 
 * @Author: Focci
 * @Date: 2023-07-20 14:44:45
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-20 14:44:45
 */

'use client'

import clsx from 'clsx'
import SVGSort from '@img/sort.svg'
import SVGMenu from '@img/menu.svg'
import SVGLocalMap from '@img/map-locale.svg'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { modeType, urls } from '@lib/parse'
import { DropdownMenuWithIcon } from '@comp/DropdownMenu'
import { useCallback, useMemo } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { map, omit, values } from 'lodash-es'
import { setListingMode } from '@lib/listing-mode'
import { getListingCategory } from '@lib/parse/urls'
import { getSortOptions } from './lib'
import { getMapListingsUrl } from '../ShowMap'

export function Dropdown({
  ...dropdownMenuWithIconProps
}) {
  return (
    <DropdownMenuWithIcon
      {...dropdownMenuWithIconProps}
    />
  )
}

function ActionButton({
  active = false,
  children,
  ...props 
}) {
  return (
    <div
      {...props}
      className={clsx([
        'relative border h-8 flex items-center gap-1 px-2 cursor-pointer',
        'hover:border-primary/50 hover:text-primary',
        active && 'border-primary text-primary z-1',
        props.className,
      ])}
    >
      {children}
    </div>
  )
}

export function SortDropdownMenu({
  isMapListing = false,
  type = '',
  searchParam = {},
  onChange = () => {},
  ...dropdownProps
}) {
  const locale = useLocale()
  const ts = useTranslations('search')
  const options = useMemo(
    () => getSortOptions(searchParam.type, ts), 
    [ts, searchParam]
  )

  const order = searchParam.order || ''
  const index = map(options, 'value').indexOf(order)
  
  const handleChange = useCallback((item) => {
    if(order === item.value) return
    const url = urls.listing(
      type, 
      { ...searchParam, order: item.value, page: 1 }, 
      locale, 
      isMapListing
    )

    onChange({ url, item })
  }, [order, type, searchParam, locale, onChange, isMapListing])
  
  return (
    <div className="flex items-center gap-1">
      <div className="hidden md:flex items-center gap-1">
        <SVGSort />
        {ts('sort')}
      </div>
      <Dropdown 
        options={options}
        value={order}
        onChange={handleChange}
        trigger={
          <ActionButton className="rounded-[.2rem]" active={index > 0}>
            {options[index]?.label}
            <ExpandMoreIcon className="size-3" />
          </ActionButton>
        }
        {...dropdownProps}
      />
    </div>
  )
}

export function ModeDropdownMenu({
  isMapListing = false,
  type = '',
  searchParam = {},
  onChange = () => {}
}) {
  const locale = useLocale()
  const ts = useTranslations('search')

  const options = useMemo(() => [
    { label: ts('singleColumnDisplay'), value: modeType.single },
    { label: ts('multiColumnDisplay'), value: modeType.listing },
  ], [ts])

  const mode = useMemo(() => (
    searchParam[modeType.listing] && !isMapListing
      ? modeType.listing
      : modeType.single
  ), [isMapListing, searchParam])

  const index = useMemo(() => map(options, 'value').indexOf(mode), [mode, options])

  /**
   * 选择单列 & 多列
   */
  const handleChange = useCallback((item) => {
    if(!isMapListing && mode === item.value) return
    
    const { value } = getListingCategory(searchParam)
    const url = urls.listing(type, { 
      ...omit(searchParam, values(modeType)), 
      [item.value]: value
    }, locale)
    
    setListingMode(item.value)
    onChange({ url, item })
  }, [mode, onChange, locale, searchParam, type, isMapListing])

  /**
   * 点击显示地图按钮
   */
  const handleMapClick = useCallback(() => {
    const url = getMapListingsUrl(type, searchParam, locale, false)
    onChange({ url })
    setListingMode(modeType.map)
  }, [locale, onChange, searchParam, type])

  return (
    <div className="flex items-center">
      <Dropdown
        options={options}
        value={mode}
        onChange={handleChange}
        trigger={
          <ActionButton 
            active={!isMapListing} 
            className={clsx([isMapListing ? 'rounded-[.2rem]' : 'rounded-l-[.2rem]'])}
          >
            <SVGMenu />
            {options[index]?.label}
          </ActionButton>
        }
      />
      {!isMapListing && (
        <ActionButton 
          active={isMapListing} 
          className="rounded-r-[.2rem]"
          onClick={handleMapClick}
        >
          <SVGLocalMap width=".8rem" />
          {ts('displayMap')}
        </ActionButton>
      )}
    </div>
  )
}
